<template>
  <div v-if="pending" class="text-center" style="transition: none !important;">
    <b-spinner />
  </div>

  <main v-else>
    <header class="justify-between align-items-stretch mb-3">
      <b-breadcrumb :items="breadcrumb" class="flex-grow-1 mb-0" />

      <b-dropdown
          v-if="$gate.allow('create', 'room') || $gate.allow('delete', item)"
          :id="'room_actions_dropdown_' + id"
          :title="$t('Actions')"
          class="ml-3"
          no-caret
          right
      >
        <template slot="button-content">
          <fa fixed-width icon="ellipsis-v" />
        </template>

        <b-dropdown-item-button
            v-if="$gate.allow('create', 'room')"
            v-b-modal.duplicate-modal
            :disabled="item.busy"
        >
          <fa fixed-width icon="copy" />
          {{ $t('Duplicate') }}
        </b-dropdown-item-button>

        <b-dropdown-item
            v-if="$gate.allow('delete', item)"
            variant="danger"
            @click="handleDelete"
        >
          <fa fixed-width icon="trash" />
          {{ $t('Delete') }}
        </b-dropdown-item>
      </b-dropdown>
    </header>

    <duplicate-modal
        v-if="$gate.allow('create', 'room')"
        id="duplicate-modal"
        :api-url="duplicateUrl"
        :project-id="projectId"
        @duplicated="handleDuplicated"
    />

    <article>
      <b-tabs content-class="mt-3">
        <b-tab :title="$t('General')">
          <b-card :title="$t('Progress')" class="mb-3">
            <b-card-sub-title class="mb-3">
              <strong>{{ completedDuration.text }}</strong>
              /
              <strong>{{ plannedDuration.text }}</strong>
            </b-card-sub-title>

            <b-progress :max="plannedDuration.value">
              <b-progress-bar :value="completedDuration.value" />
            </b-progress>
          </b-card>

          <b-card class="mb-3" no-body>
            <b-form
                @keydown="item.onKeydown($event)"
                @submit.prevent="handleSave"
            >
              <b-card-body>
                <b-form-group
                    :invalid-feedback="item.errors.get('name')"
                    :label="$t('Name')"
                    :state="item.errors.has('name') ? false : null"
                    label-for="name"
                >
                  <b-form-input
                      id="name"
                      v-model="item.name"
                      :readonly="$gate.deny('update', item)"
                      :state="item.errors.has('name') ? false : null"
                      autofocus
                      name="name"
                  />
                </b-form-group>

                <b-form-group
                    :invalid-feedback="item.errors.get('short_name')"
                    :label="$t('Short name')"
                    :state="item.errors.has('short_name') ? false : null"
                    label-for="short_name"
                >
                  <b-form-input
                      id="short_name"
                      v-model="item.short_name"
                      :readonly="$gate.deny('update', item)"
                      :state="item.errors.has('short_name') ? false : null"
                      maxlength="5"
                      name="short_name"
                  />
                </b-form-group>

                <b-form-group
                    :invalid-feedback="item.errors.get('height')"
                    :label="$t('Height')"
                    :state="item.errors.has('height') ? false : null"
                    label-for="height"
                >
                  <b-input-group append="m">
                    <b-form-input
                        id="height"
                        v-model="item.height"
                        :readonly="$gate.deny('update', item)"
                        :state="item.errors.has('height') ? false : null"
                        min="0"
                        name="height"
                        step="0.001"
                        type="number"
                    />
                  </b-input-group>
                </b-form-group>

                <b-form-group
                    :invalid-feedback="item.errors.get('floor_space')"
                    :label="$t('Floor space')"
                    :state="item.errors.has('floor_space') ? false : null"
                    label-for="floor_space"
                >
                  <b-input-group>
                    <b-form-input
                        id="floor_space"
                        v-model="item.floor_space"
                        :readonly="$gate.deny('update', item)"
                        :state="item.errors.has('floor_space') ? false : null"
                        min="0"
                        name="floor_space"
                        step="0.001"
                        type="number"
                    />
                    <b-input-group-text slot="append">
                      m<sup>2</sup>
                    </b-input-group-text>
                  </b-input-group>
                </b-form-group>
              </b-card-body>

              <b-card-footer v-if="$gate.allow('update', item)">
                <b-button :disabled="item.busy" type="submit" variant="primary">
                  {{ $t('Save') }}
                </b-button>
              </b-card-footer>
            </b-form>
          </b-card>
        </b-tab>

        <b-tab :title="$t('Overview')">
          <overview
              v-if="id"
              :api-url="itemUrl + '/overview'"
              :fields="['wall_name', 'element_name']"
              class="mb-3"
          />
        </b-tab>

        <b-tab :title="$t('Elements')">
          <room-elements :room-id="id" />
        </b-tab>

        <b-tab :title="$t('Disabilities')">
          <header class="justify-between mb-3">
            <h1 class="h5 mb-0">
              {{ $t('Disabilities') }}
            </h1>

            <b-button-group>
              <b-button
                  :disabled="pending"
                  @click="fetchData"
              >
                <fa :spin="pending" icon="sync" />
              </b-button>
            </b-button-group>
          </header>
          
          <room-disabilities :room-id="id" />
        </b-tab>

        <measurement :api-url="`${itemUrl}/d11e`" />
      </b-tabs>
    </article>
  </main>
</template>

<script>
import Form from 'vform';
import Overview from '@/components/Overview';
import DuplicateModal from '@/components/DuplicateModal';
import RoomDisabilities from "@/components/rooms/RoomDisabilities";
import Measurement from "@/components/Measurement";
import RoomElements from "@/components/rooms/RoomElements";
import itemMixin from "@/mixins/itemMixin";

export default {
  name: 'Room',
  components: {RoomElements, Measurement, RoomDisabilities, DuplicateModal, Overview},
  mixins: [itemMixin],
  props: {
    projectId: {
      type: [Number, String],
      required: true,
    },
    componentId: {
      type: [Number, String],
      required: true,
    },
    floorId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      item: new Form({
        name: null,
        short_name: null,
        height: null,
        floor_space: null,
        model_name: null
      }),
      completedDuration: {},
      plannedDuration: {},
    };
  },
  computed: {
    baseUrl() {
      return `/api/projects/${this.projectId}/components/${this.componentId}/floors/${this.floorId}/rooms`;
    },
    resourceName() {
      return 'rooms'
    },
  },
  methods: {
    afterDelete() {
      this.$router.push({
        name: 'projects.components.floors.show',
        params: {
          projectId: this.projectId,
          componentId: this.componentId,
          floorId: this.floorId,
        },
      });
    },

    handleDuplicated(data) {
      if (!(data && data.id)) {
        return;
      }

      this.$router.push({
        name: 'projects.components.floors.rooms.show',
        params: {
          projectId: this.projectId,
          componentId: this.componentId,
          floorId: this.floorId,
          roomId: data.id,
        },
      });
    },

    setData(data) {
      this.completedDuration = data.completedDuration;
      this.plannedDuration = data.plannedDuration;
      this.breadcrumb = [
        {
          text: this.$t('Projects'),
          to: {name: 'projects.index'}
        }
      ].concat(data.breadcrumb);
      this.item.fill(data);
    },
  },
};
</script>
